'use client';

import { useState, useEffect, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import CategorySidebar from './CategorySidebar';
import ProductFilters from './ProductFilters';
import ProductGrid from './ProductGrid';
import Icon from '@/components/ui/AppIcon';
import { useProducts } from '@/hooks/useProducts';

export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  alt: string;
  category: string | string[];
  subcategory?: string;
  rating: number;
  reviewCount: number;
  description: string;
  tags: string[];
  isNew?: boolean;
  isPopular?: boolean;
  isVegan?: boolean;
  allergens: string[];
}

// Helper function to check if product belongs to category
const belongsToCategory = (product: Product, categoryId: string): boolean => {
  const categories = Array.isArray(product.category) ? product.category : [product.category];
  return categories.some(cat => cat.toLowerCase() === categoryId);
};

const sortOptions = [
  { value: 'name', label: 'Name (A-Z)' },
  { value: 'price-low', label: 'Price (Low to High)' },
  { value: 'price-high', label: 'Price (High to Low)' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'newest', label: 'Newest First' }
];

export default function ProductsPageInteractive() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('name');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 4200]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);

  // Fetch products from database
  const { products: fetchedProducts, isLoading } = useProducts();

  // Convert detailed products to the format expected by products page
  const products: Product[] = useMemo(() => {
    return fetchedProducts.map(product => {
      return {
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: product.images[0]?.url || '',
        alt: product.name,
        category: product.category,
        subcategory: product.subcategory || (Array.isArray(product.category) ? product.category[0] : product.category),
        rating: product.rating,
        reviewCount: product.reviewCount,
        description: product.description,
        tags: product.tags || ['Vegetarian'],
        isNew: product.isNew || false,
        isPopular: product.isPopular || false,
        isVegan: product.isVegan || false,
        allergens: product.allergens || []
      };
    });
  }, [fetchedProducts]);

  // Dynamic categories based on loaded products
  const categories = useMemo(() => [
    { id: 'all', name: 'All Products', count: products.length },
    { id: 'cup cakes', name: 'Cup Cakes', count: products.filter(p => belongsToCategory(p, 'cup cakes')).length },
    { id: 'jar cakes', name: 'Jar Cakes', count: products.filter(p => belongsToCategory(p, 'jar cakes')).length },
    { id: 'pastries', name: 'Pastries', count: products.filter(p => belongsToCategory(p, 'pastries')).length },
    { id: 'cakes', name: 'Cakes', count: products.filter(p => belongsToCategory(p, 'cakes')).length },
    { id: 'cheesecakes', name: 'Cheesecakes', count: products.filter(p => belongsToCategory(p, 'cheesecakes')).length },
    { id: 'brownie', name: 'Brownie', count: products.filter(p => belongsToCategory(p, 'brownie')).length },
    { id: 'cookies', name: 'Cookies', count: products.filter(p => belongsToCategory(p, 'cookies')).length },
    { id: 'tea cakes', name: 'Tea Cakes', count: products.filter(p => belongsToCategory(p, 'tea cakes')).length }
  ], [products]);

  // Update selectedCategory when URL parameter changes
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    setSelectedCategory(categoryParam || 'all');
  }, [searchParams]);

  useEffect(() => {
    const searchParam = searchParams.get('search') || '';
    setSearchTerm(searchParam);
  }, [searchParams]);

  // Get all unique tags for filtering
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    products.forEach(product => {
      product.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [products]);

  const searchSuggestions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return [];
    }

    return products
      .filter((product) => {
        const inName = product.name.toLowerCase().includes(normalizedSearch);
        const categories = Array.isArray(product.category) ? product.category : [product.category];
        const inCategory = categories.some((cat) => cat.toLowerCase().includes(normalizedSearch));
        const inSubcategory = product.subcategory?.toLowerCase().includes(normalizedSearch);
        const inTags = product.tags.some((tag) => tag.toLowerCase().includes(normalizedSearch));
        return inName || inCategory || inSubcategory || inTags;
      })
      .slice(0, 5);
  }, [products, searchTerm]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products;
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (normalizedSearch) {
      filtered = filtered.filter(product => {
        const inName = product.name.toLowerCase().includes(normalizedSearch);
        const categories = Array.isArray(product.category) ? product.category : [product.category];
        const inCategory = categories.some(cat => cat.toLowerCase().includes(normalizedSearch));
        const inSubcategory = product.subcategory?.toLowerCase().includes(normalizedSearch);
        const inTags = product.tags.some(tag => tag.toLowerCase().includes(normalizedSearch));
        return inName || inCategory || inSubcategory || inTags;
      });
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product =>
        belongsToCategory(product, selectedCategory)
      );
    }

    // Price range filter
    filtered = filtered.filter(product =>
      product.price >= priceRange[0] && product.price <= priceRange[1]
    );

    // Tags filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(product =>
        selectedTags.some(tag => product.tags.includes(tag))
      );
    }

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'rating':
          return b.rating - a.rating;
        case 'newest':
          // For demo, sort by ID (assuming higher ID = newer)
          return parseInt(b.id) - parseInt(a.id);
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [products, selectedCategory, sortBy, priceRange, selectedTags, searchTerm]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSearchSubmit = () => {
    const firstMatch = searchSuggestions[0];

    if (firstMatch) {
      router.push(`/product-details?id=${encodeURIComponent(firstMatch.id)}`);
      return;
    }

    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) {
      setShowSearchSuggestions(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div data-component-id="src/app/products/components/ProductsPageInteractive.tsx:214:4" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="214" data-component-end-line="306" data-component-file="ProductsPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20gap-8%22%7D" className="flex flex-col lg:flex-row gap-8">
      {/* Mobile Filter Toggle + Search */}
      <div data-component-id="src/app/products/components/ProductsPageInteractive.tsx:216:6" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="216" data-component-end-line="273" data-component-file="ProductsPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%20flex%20items-center%20gap-3%20mb-1%20w-full%22%7D" className="lg:hidden flex items-center gap-3 mb-1 w-full">
        <button data-component-id="src/app/products/components/ProductsPageInteractive.tsx:217:8" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="217" data-component-end-line="223" data-component-file="ProductsPageInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20px-4%20py-2%20bg-primary%20text-primary-foreground%20rounded-md%20hover%3Abg-primary%2F90%20transition-colors%20shrink-0%22%2C%22textContent%22%3A%22Filters%22%7D"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors shrink-0"
        >
          <Icon data-component-id="src/app/products/components/ProductsPageInteractive.tsx:221:10" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="221" data-component-end-line="221" data-component-file="ProductsPageInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22FunnelIcon%22%7D" name="FunnelIcon" size={20} />
          Filters
        </button>
        <div data-component-id="src/app/products/components/ProductsPageInteractive.tsx:224:8" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="224" data-component-end-line="272" data-component-file="ProductsPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20flex-1%20min-w-0%22%7D" className="relative flex-1 min-w-0">
          <Icon data-component-id="src/app/products/components/ProductsPageInteractive.tsx:225:10" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="225" data-component-end-line="229" data-component-file="ProductsPageInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-3%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
            name="MagnifyingGlassIcon"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input data-component-id="src/app/products/components/ProductsPageInteractive.tsx:230:10" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="230" data-component-end-line="249" data-component-file="ProductsPageInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22search%22%2C%22value%22%3A%22%5Bvar%3AsearchTerm%5D%22%2C%22className%22%3A%22w-full%20h-10%20pl-10%20pr-3%20border%20border-border%20rounded-md%20bg-card%20text-foreground%20shadow-warm-sm%20focus%3Aoutline-none%20focus%3Aring-3%20focus%3Aring-ring%20focus%3Aborder-primary%20transition-all%22%7D"
            type="search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowSearchSuggestions(true);
            }}
            onFocus={() => setShowSearchSuggestions(true)}
            onBlur={() => {
              window.setTimeout(() => setShowSearchSuggestions(false), 150);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearchSubmit();
              }
            }}
            placeholder="Search products"
            className="w-full h-10 pl-10 pr-3 border border-border rounded-md bg-card text-foreground shadow-warm-sm focus:outline-none focus:ring-3 focus:ring-ring focus:border-primary transition-all"
          />
          {showSearchSuggestions && searchTerm.trim() && searchSuggestions.length > 0 && (
            <div data-component-id="src/app/products/components/ProductsPageInteractive.tsx:251:12" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="251" data-component-end-line="270" data-component-file="ProductsPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20left-0%20right-0%20top-full%20mt-2%20z-20%20overflow-hidden%20rounded-md%20border%20border-border%20bg-popover%20shadow-warm-lg%22%7D" className="absolute left-0 right-0 top-full mt-2 z-20 overflow-hidden rounded-md border border-border bg-popover shadow-warm-lg">
              {searchSuggestions.map((product) => (
                <button data-component-id="src/app/products/components/ProductsPageInteractive.tsx:253:16" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="253" data-component-end-line="268" data-component-file="ProductsPageInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22flex%20w-full%20items-center%20gap-3%20px-3%20py-3%20text-left%20transition-colors%20hover%3Abg-primary%2F5%20focus%3Aoutline-none%20focus%3Abg-primary%2F5%22%7D"
                  key={product.id}
                  type="button"
                  onClick={() => router.push(`/product-details?id=${encodeURIComponent(product.id)}`)}
                  className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-primary/5 focus:outline-none focus:bg-primary/5"
                >
                  <div data-component-id="src/app/products/components/ProductsPageInteractive.tsx:259:18" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="259" data-component-end-line="263" data-component-file="ProductsPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-10%20w-10%20flex-shrink-0%20overflow-hidden%20rounded-md%20bg-muted%22%7D" className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md bg-muted">
                    {product.image ? (
                      <img data-component-id="src/app/products/components/ProductsPageInteractive.tsx:261:22" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="261" data-component-end-line="261" data-component-file="ProductsPageInteractive.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22h-full%20w-full%20object-cover%22%7D" src={product.image} alt={product.alt} className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div data-component-id="src/app/products/components/ProductsPageInteractive.tsx:264:18" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="264" data-component-end-line="267" data-component-file="ProductsPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%20flex-1%22%7D" className="min-w-0 flex-1">
                    <p data-component-id="src/app/products/components/ProductsPageInteractive.tsx:265:20" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="265" data-component-end-line="265" data-component-file="ProductsPageInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22truncate%20text-sm%20font-medium%20text-foreground%22%7D" className="truncate text-sm font-medium text-foreground">{product.name}</p>
                    <p data-component-id="src/app/products/components/ProductsPageInteractive.tsx:266:20" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="266" data-component-end-line="266" data-component-file="ProductsPageInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22truncate%20text-xs%20text-muted-foreground%22%7D" className="truncate text-xs text-muted-foreground">{product.subcategory || product.category}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div data-component-id="src/app/products/components/ProductsPageInteractive.tsx:276:6" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="276" data-component-end-line="287" data-component-file="ProductsPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`lg:block ${showFilters ? 'block' : 'hidden'}`}>
        <CategorySidebar data-component-id="src/app/products/components/ProductsPageInteractive.tsx:277:8" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="277" data-component-end-line="286" data-component-file="ProductsPageInteractive.tsx" data-component-name="CategorySidebar" data-component-content="%7B%22elementName%22%3A%22CategorySidebar%22%7D"
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={(category) => {
            setSelectedCategory(category);
            setShowFilters(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onClose={() => setShowFilters(false)}
        />
      </div>

      {/* Main Content */}
      <div data-component-id="src/app/products/components/ProductsPageInteractive.tsx:290:6" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="290" data-component-end-line="305" data-component-file="ProductsPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
        {/* Filters */}
        <ProductFilters data-component-id="src/app/products/components/ProductsPageInteractive.tsx:292:8" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="292" data-component-end-line="301" data-component-file="ProductsPageInteractive.tsx" data-component-name="ProductFilters" data-component-content="%7B%22elementName%22%3A%22ProductFilters%22%7D"
          sortBy={sortBy}
          onSortChange={setSortBy}
          priceRange={priceRange}
          onPriceRangeChange={setPriceRange}
          selectedTags={selectedTags}
          onTagToggle={handleTagToggle}
          allTags={allTags}
          totalProducts={filteredAndSortedProducts.length}
        />

        {/* Product Grid */}
        <ProductGrid data-component-id="src/app/products/components/ProductsPageInteractive.tsx:304:8" data-component-path="src/app/products/components/ProductsPageInteractive.tsx" data-component-line="304" data-component-end-line="304" data-component-file="ProductsPageInteractive.tsx" data-component-name="ProductGrid" data-component-content="%7B%22elementName%22%3A%22ProductGrid%22%7D" products={filteredAndSortedProducts} isLoading={isLoading} />
      </div>
    </div>
  );
}